import { FC, useEffect } from "react";
import { Controller } from "react-hook-form";
import { useI18n } from "../../hooks/useTranslations";
import { Label } from "../../http/label/types";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import Input from "../ui/Input";
import Numeric from "../ui/Numeric";

export interface LabelFormBody {
  code: string;
  cost: number;
}

type LabelFormProps = {
  label?: Label;
  onSubmit: (data: LabelFormBody) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const LabelForm: FC<LabelFormProps> = ({ label, onSubmit, isDisabled, isLoading }) => {
  const i18n = useI18n();

  const defaultValues = {
    code: label?.code ?? "",
    cost: label?.cost ?? 0,
  };

  return (
    <Form<LabelFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
    >
      {({ control, setValue, reset }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [label]);

        return (
          <>
            <Controller
              name="code"
              control={control}
              rules={{ required: i18n.t.common.fieldCannotBeEmpty }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.code} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Input id={labelId} {...field} isInvalid={isInvalid} disabled={isDisabled || isLoading} />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="cost"
              control={control}
              rules={{
                required: true,
                min: {
                  value: 0.0001,
                  message: i18n.t.common.fieldCannotBeEmpty,
                },
              }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.cost} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      min={0.0001}
                      decimalScale={4}
                      allowNegative={false}
                      isInvalid={isInvalid}
                      disabled={isDisabled || isLoading}
                      suffix=" €"
                      onBlur={(value) => setValue("cost", value ?? 0)}
                    />
                  )}
                </FormField>
              )}
            />
          </>
        );
      }}
    </Form>
  );
};

export default LabelForm;
